import {fromEquals, getStructEq} from 'fp-ts/lib/Eq';
import {flow} from 'fp-ts/lib/function';
import * as O from 'fp-ts/lib/Option';
import _, {isEqual, last, maxBy} from 'lodash';
import {createSelector} from 'reselect';

import {CellsConversationsBoundary} from '../../../helpers/cellsHelpers';
import {ExponentialBackoffOptions, withExponentialBackoff} from '../../../helpers/controlHelpers';
import {conversationSortPropertiesInPast} from '../../../models/api/conversationCommonTypes';
import {CellsSortDirectionsEnum} from '../../../models/api/sortOptionModel';
import {InboxesFiltersEnum} from '../../../models/url/inboxesUrlModel';
import {Dispatch} from '../../../store/appStore';
import {getHasMoreP2Prefetch} from '../../../store/featureFlags/featureFlagsSelectors';
import {
  CellsListState,
  isCellsConversationsList,
} from '../../../store/metadata/cellsLists/cellsList/cellsListState';
import {prefetchMoreConversationsAsyncAction} from '../../../store/metadata/cellsLists/cellsListsActions';
import {selectCellsListRangesTop} from '../../../store/metadata/cellsLists/cellsListsHelpers';
import {getCellsLists} from '../../../store/metadata/cellsLists/cellsListsSelectors';
import {InboxesConversationsParameters} from '../../../store/panels/inboxes/parameters/inboxesParameters';
import {areParametersCellsEqual} from '../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {isHttpTooManyRequestsError} from '../../http/httpError';
import {Prefetcher} from './base';

/*
 * Factory.
 */

interface MoreCellsCondition {
  parameters: InboxesConversationsParameters;
  bottomBoundary: CellsConversationsBoundary;
}

/** Creates a prefetcher that prefetches an additional page of the shortest P2. */
export function createMoreCellsPrefetcher(
  filter: InboxesFiltersEnum,
  maxAge: number,
  exponentialBackoffOptions: ExponentialBackoffOptions,
): Prefetcher<MoreCellsCondition> {
  // Create a memoized selector that finds all eligible P2 lists.
  const getMoreCellsConditions = makeGetMoreCellsConditions(filter);

  // Filters the eligible P2 lists based on the current date.
  const filterCellsConditionsByAge = (conditions: ReadonlyArray<MoreCellsCondition>) => {
    const minDate = Date.now() - maxAge;

    return conditions.filter((condition) => condition.bottomBoundary.date >= minDate);
  };

  return {
    key: `more-cells-${filter}`,
    computeBacklogLength: flow(
      getMoreCellsConditions,
      filterCellsConditionsByAge,
      (conditions) => conditions.length,
    ),
    computeCondition: flow(
      getMoreCellsConditions,
      filterCellsConditionsByAge,
      (conditions) => maxBy(conditions, (condition) => condition.bottomBoundary.date),
      O.fromNullable,
    ),
    C: eqMoreCellsCondition,
    prefetchAsync: (dispatch, condition) =>
      prefetchMoreCellsAsync(dispatch, condition, exponentialBackoffOptions),
  };
}

/*
 * Helpers.
 */

const eqMoreCellsCondition = getStructEq<MoreCellsCondition>({
  parameters: fromEquals(areParametersCellsEqual),
  bottomBoundary: fromEquals(isEqual),
});

function makeGetMoreCellsConditions(filter: InboxesFiltersEnum) {
  return createSelector([getCellsLists, getHasMoreP2Prefetch], (lists, hasMoreP2Prefetch) => {
    if (!hasMoreP2Prefetch) {
      return [];
    }

    return _(lists)
      .map((list) => computeMoreCellsCondition(list, filter))
      .compact()
      .value();
  });
}

function computeMoreCellsCondition(
  list: CellsListState,
  filter: InboxesFiltersEnum,
): MoreCellsCondition | undefined {
  // Only prefetch eligible P2 lists.
  const eligibility = computeMoreCellsEligibility(list, filter);
  if (!eligibility) {
    return undefined;
  }

  const {parameters, bottomBoundary} = eligibility;

  // Only prefetch P2 lists that have more at the bottom.
  if (!bottomBoundary) {
    return undefined;
  }

  return {
    parameters,
    bottomBoundary,
  };
}

interface MoreCellsEligibility {
  parameters: InboxesConversationsParameters;
  bottomBoundary: CellsConversationsBoundary | undefined;
}

export function computeMoreCellsEligibility(
  list: CellsListState,
  filter: InboxesFiltersEnum,
): MoreCellsEligibility | undefined {
  // Only prefetch conversation lists.
  if (!isCellsConversationsList(list)) {
    return undefined;
  }

  // Only prefetch P2 lists that match our filter.
  const {parameters} = list;
  if (parameters.filters.length !== 1 || parameters.filters[0] !== filter) {
    return undefined;
  }

  // Only prefetch P2 lists that go from newer -> older by a date that is in the past.
  const isSortedDescendingFromNow =
    parameters.sortDirection === CellsSortDirectionsEnum.DESCENDING &&
    conversationSortPropertiesInPast.has(parameters.sortProperty);
  if (!isSortedDescendingFromNow) {
    return undefined;
  }

  // Only prefetch non-empty P2 lists.
  const ranges = selectCellsListRangesTop(parameters.sortDirection, list.ranges);
  const lastRange = last(ranges);
  if (!lastRange) {
    return undefined;
  }

  const {bottomBoundary} = lastRange;
  return {
    parameters,
    bottomBoundary,
  };
}

export async function prefetchMoreCellsAsync(
  dispatch: Dispatch,
  condition: MoreCellsCondition,
  exponentialBackoffOptions: ExponentialBackoffOptions,
): Promise<void> {
  const {parameters, bottomBoundary} = condition;

  return withExponentialBackoff(
    () => dispatch(prefetchMoreConversationsAsyncAction(parameters, bottomBoundary)),
    isHttpTooManyRequestsError,
    exponentialBackoffOptions,
  );
}
